import React from 'react';
import { GlobalNavigation, HeaderShadowWrapper, PageHeader, SubNavigation } from 'ui-toolkit';
import { UUID } from '../../../types/shared/UUID';
import { useLocation, useParams } from 'react-router-dom';
import {
  determinePartiesToShow,
  generatePageHeaderDataPoints,
} from '../../../utilities/clientPortal/generatePageHeaderDataPoints';
import { generateBottomTags } from '../../../utilities/clientPortal/generateTags';
import { determineActiveMainAndActiveSub } from '../../../utilities/clientPortal/determineActiveMainAndActiveSub';
import { generateNavigationItems } from '../../../utilities/clientPortal/generateNavigationItems';
import { ClientPageHeaderMiniNavigation } from './ClientPageHeaderMiniNavigation';
import { propertyAddressToString } from '../../../ui-toolkit/orders/interfaces/IPropertyAddress';
import { FullName } from '../../../ui-toolkit/orders/interfaces/IParty';
import { IOrderDetailsData } from '../../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { IHasTestData } from '../../../ui-toolkit/test-utilities/IHasTestData';
import { mergeTests } from '../../../ui-toolkit/test-utilities/mergeTests';
import { useHasFeatureFlag } from '../../../selectors/useHasFeatureFlag';

interface IClientOrderPageHeaderProps extends IHasTestData {
  showSub: boolean;
  creator?: string;
  order: IOrderDetailsData;
  onLinkClick?: (href?: string) => void;
  notificationPanel?: React.ReactNode;
  notificationSubscription?: React.ReactNode;
  profile?: React.ReactNode;
  children?: React.ReactNode;
}

export const ClientOrderPageHeader: React.FC<IClientOrderPageHeaderProps> = ({
  showSub,
  creator,
  order: { parties, property, orderInformation, transaction, lender },
  onLinkClick,
  children,
  'data-test-id': dataTestID,
}) => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const pathname = useLocation().pathname;
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const { activeMain, activeSub } = determineActiveMainAndActiveSub(pathname);

  const navigationItems = generateNavigationItems(orderId);
  const bottomTags = generateBottomTags(orderInformation.externalStatus, lender.name, dataTestID);

  const names = determinePartiesToShow(parties).map(FullName).join(', ');

  return (
    <>
      <ClientPageHeaderMiniNavigation
        activeSub={activeSub}
        navigationItems={navigationItems}
        property={property}
        names={names}
        fileNumber={transaction.file_number}
        showSub={showSub}
      />
      <HeaderShadowWrapper useDefaultBackground={isZillowClosingEnabled}>
        <GlobalNavigation active={activeMain} onClick={onLinkClick} isLogoLink />
        <PageHeader
          data-test-id={mergeTests(dataTestID, 'lender-page-header')}
          bottomTags={bottomTags}
          headerText={propertyAddressToString(property)}
          dataPoints={generatePageHeaderDataPoints(transaction, parties, creator, property.countyName)}
        >
          {children}
        </PageHeader>
        <SubNavigation active={activeSub}>{showSub ? navigationItems : undefined}</SubNavigation>
      </HeaderShadowWrapper>
    </>
  );
};
